/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    RecordingsStatusApi,
    RecordingsStatusGetRequest,
} from 'client-code/recordings-status';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IRecordingsStatusServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TRecordingsStatusDetails, TServicesParams } from '../types';

export class RecordingsStatusServiceRepositoryImpl implements IRecordingsStatusServiceRepository {
    private static readonly TAG: string = 'RecordingsStatusServiceRepositoryImpl';

    private readonly mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;
    }

    public async fetchRecordingsStatus(): Promise<TRecordingsStatusDetails> {
        try {
            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;
            const configurationParameters: ConfigurationParameters = {
                basePath: this.mServiceParams.basePath,
                apiKey: this.mServiceParams.accessToken,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };
            const configuration = new Configuration(configurationParameters);
            const recordingsStatusApi = new RecordingsStatusApi(configuration);
            const requestParams: RecordingsStatusGetRequest = {
                applicationName: commonHeaders.ApplicationName,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
                bodyId: this.mServiceParams.bodyId,
                whiny: this.mServiceParams.whiny || 'false',
            };

            const recordingsStatusResponse =
                await recordingsStatusApi.recordingsStatusGet(requestParams);
            const maxAge = Number(
                recordingsStatusResponse.headers?.['cache-control'].split('=')?.[1] || 0,
            );
            return { maxAge, recordingsStatus: recordingsStatusResponse.data };
        } catch (error: any) {
            Log.e(
                RecordingsStatusServiceRepositoryImpl.TAG,
                'RecordingsStatusServiceRepositoryImpl::fetchRecordingsStatus',
                error,
            );
            throw error;
        }
    }
}
